// hooks/useDepartments.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getDepartmentTree,
  getSelectableDepartmentTree,
  getDepartmentUsers,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  moveDepartment,
  addUsersToDepartment,
  removeUserFromDepartment,
} from '@/api/departments'
import type {
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
  MoveDepartmentRequest,
} from '@/types/department'

// ============================================
// Query Keys
// ============================================
export const departmentKeys = {
  all: ['departments'] as const,
  tree: () => [...departmentKeys.all, 'tree'] as const,
  selectableTree: () => [...departmentKeys.all, 'selectable-tree'] as const,
  detail: (id: number) => [...departmentKeys.all, 'detail', id] as const,
  users: (id: number) => [...departmentKeys.all, 'users', id] as const,
}

// ============================================
// 获取部门树 Hook
// ============================================
export function useDepartmentTree() {
  return useQuery({
    queryKey: departmentKeys.tree(),
    queryFn: getDepartmentTree,
  })
}

export function useSelectableDepartmentTree() {
  return useQuery({
    queryKey: departmentKeys.selectableTree(),
    queryFn: getSelectableDepartmentTree,
  })
}

// ============================================
// 获取部门用户列表 Hook
// ============================================
export function useDepartmentUsers(departmentId: number | undefined) {
  return useQuery({
    queryKey: departmentKeys.users(departmentId!),
    queryFn: () => getDepartmentUsers(departmentId!),
    enabled: !!departmentId,
  })
}

// ============================================
// 创建部门 Hook
// ============================================
export function useCreateDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDepartmentRequest) => createDepartment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.tree() })
    },
  })
}

// ============================================
// 更新部门 Hook
// ============================================
export function useUpdateDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDepartmentRequest }) =>
      updateDepartment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.tree() })
      queryClient.invalidateQueries({ queryKey: departmentKeys.detail(id) })
    },
  })
}

// ============================================
// 删除部门 Hook
// ============================================
export function useDeleteDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => deleteDepartment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.tree() })
    },
  })
}

// ============================================
// 移动部门 Hook
// ============================================
export function useMoveDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MoveDepartmentRequest }) =>
      moveDepartment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.tree() })
    },
  })
}

// ============================================
// 添加用户到部门 Hook
// ============================================
export function useAddUsersToDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ departmentId, userIds }: { departmentId: number; userIds: number[] }) =>
      addUsersToDepartment(departmentId, userIds),
    onSuccess: (_, { departmentId }) => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.users(departmentId) })
      queryClient.invalidateQueries({ queryKey: departmentKeys.tree() })
    },
  })
}

// ============================================
// 移除部门用户 Hook
// ============================================
export function useRemoveUserFromDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ departmentId, userId }: { departmentId: number; userId: number }) =>
      removeUserFromDepartment(departmentId, userId),
    onSuccess: (_, { departmentId }) => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.users(departmentId) })
      queryClient.invalidateQueries({ queryKey: departmentKeys.tree() })
    },
  })
}
